import { useState, lazy, Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Mail, Menu, X, ChevronDown } from "lucide-react";

const NavigationAuth = lazy(() => import("./NavigationAuth"));

interface NavLink {
  label: string;
  path: string;
}

const NavDropdown = ({ label, links, isActive }: { label: string; links: NavLink[]; isActive: boolean }) => (
  <div className="relative group">
    <button className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
      isActive ? "bg-muted text-primary" : "text-foreground/80 hover:text-foreground hover:bg-muted/50"
    }`}>
      {label}
      <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
    </button>
    <div className="absolute left-0 top-full pt-1 hidden group-hover:block group-focus-within:block z-50">
      <div className="w-56 rounded-md border bg-popover p-1 shadow-md">
        {links.map(link => (
          <Link key={link.path} to={link.path} className="block px-3 py-2 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground transition-colors">
            {link.label}
          </Link>
        ))}
      </div>
    </div>
  </div>
);

export const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const location = useLocation();
  
  const systemsLinks: NavLink[] = [
    { label: "AI Growth Stack", path: "/ai-systems" },
    { label: "Framework", path: "/framework" },
  ];

  const modelsLinks: NavLink[] = [
    { label: "MAD-M Decay Model", path: "/decay-model" },
    { label: "MAHI™ Index", path: "/mahi-index" },
    { label: "Research & White Papers", path: "/research" },
  ];

  const proofLinks: NavLink[] = [
    { label: "Case Studies", path: "/case-studies" },
    { label: "Insights", path: "/about/insights" },
  ];

  const dossierLinks: NavLink[] = [
    { label: "About", path: "/about" },
    { label: "The Engagement Protocol", path: "/engagement-protocol" },
    { label: "Executive Strategy", path: "/strategy" },
  ];
  
  const isActivePath = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isAnyLinkActive = (links: NavLink[]) => {
    return links.some(link => isActivePath(link.path));
  };

  return <>
      <header className="sticky top-0 z-[100] w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 relative">
        <nav className="container mx-auto px-6" aria-label="Main navigation">
          <div className="flex h-16 items-center justify-between">
            {/* Logo/Brand */}
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <span className="text-lg font-semibold text-foreground">
                Kristina Shrider
              </span>
              <span className="hidden sm:inline text-sm text-muted-foreground font-normal">| Independent AI Marketing Researcher</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              <Link to="/" className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${isActivePath("/") && location.pathname === "/" ? "bg-muted text-primary" : "text-foreground/80 hover:text-foreground hover:bg-muted/50"}`}>
                Home
              </Link>
              
              <NavDropdown label="The Systems" links={systemsLinks} isActive={isAnyLinkActive(systemsLinks)} />
              <NavDropdown label="The Models" links={modelsLinks} isActive={isAnyLinkActive(modelsLinks)} />
              <NavDropdown label="The Proof" links={proofLinks} isActive={isAnyLinkActive(proofLinks)} />
              <NavDropdown label="The Dossier" links={dossierLinks} isActive={isAnyLinkActive(dossierLinks)} />
              
              <Link to="/contact" className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${isActivePath("/contact") ? "bg-muted text-primary" : "text-foreground/80 hover:text-foreground hover:bg-muted/50"}`}>
                Contact
              </Link>
            </div>

            {/* Desktop Auth & CTA */}
            <div className="hidden md:flex items-center gap-3">
              <Suspense fallback={null}>
                <NavigationAuth authDialogOpen={authDialogOpen} setAuthDialogOpen={setAuthDialogOpen} />
              </Suspense>

              <Button size="sm" asChild>
                <a href="https://koalendar.com/e/meet-with-kristina-shrider" target="_blank" rel="noopener noreferrer">
                  <Mail className="h-4 w-4 mr-2" />
                  Request Strategic Briefing
                </a>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <Button variant="ghost" size="sm" className="md:hidden z-[100]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={mobileMenuOpen ? "Close main menu" : "Open main menu"}>
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </nav>

        {/* Mobile Menu - outside nav for proper absolute positioning */}
        {mobileMenuOpen && <div className="md:hidden absolute left-0 right-0 top-full w-full border-b shadow-lg z-[100] animate-fade-in" style={{ backgroundColor: 'hsl(0, 0%, 99%)' }}>
            <div className="flex flex-col space-y-2 px-6 py-4 max-h-[80vh] overflow-y-auto">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === "/" ? "bg-muted text-primary" : "text-foreground/80 hover:text-foreground hover:bg-muted/50"}`}>
                Home
              </Link>
              
              {/* Mobile The Systems Section */}
              <Link to="/framework" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors">
                The Systems
              </Link>
              {systemsLinks.map(link => <Link key={link.path} to={link.path} onClick={() => setMobileMenuOpen(false)} className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${isActivePath(link.path) ? "bg-muted text-primary" : "text-foreground/80 hover:text-foreground hover:bg-muted/50"}`}>
                  {link.label}
                </Link>)}

              {/* Mobile The Models Section */}
              <Link to="/framework" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2 hover:text-foreground transition-colors">
                The Models
              </Link>
              {modelsLinks.map(link => <Link key={link.path} to={link.path} onClick={() => setMobileMenuOpen(false)} className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${isActivePath(link.path) ? "bg-muted text-primary" : "text-foreground/80 hover:text-foreground hover:bg-muted/50"}`}>
                  {link.label}
                </Link>)}

              {/* Mobile The Proof Section */}
              <Link to="/case-studies" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2 hover:text-foreground transition-colors">
                The Proof
              </Link>
              {proofLinks.map(link => <Link key={link.path} to={link.path} onClick={() => setMobileMenuOpen(false)} className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${isActivePath(link.path) ? "bg-muted text-primary" : "text-foreground/80 hover:text-foreground hover:bg-muted/50"}`}>
                  {link.label}
                </Link>)}

              {/* Mobile The Dossier Section */}
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-2 hover:text-foreground transition-colors">
                The Dossier
              </Link>
              {dossierLinks.map(link => <Link key={link.path} to={link.path} onClick={() => setMobileMenuOpen(false)} className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${isActivePath(link.path) ? "bg-muted text-primary" : "text-foreground/80 hover:text-foreground hover:bg-muted/50"}`}>
                  {link.label}
                </Link>)}
              
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${isActivePath("/contact") ? "bg-muted text-primary" : "text-foreground/80 hover:text-foreground hover:bg-muted/50"}`}>
                Contact
              </Link>
              
              <div className="pt-4 flex flex-col gap-2 border-t">
                <Suspense fallback={null}>
                  <NavigationAuth authDialogOpen={authDialogOpen} setAuthDialogOpen={setAuthDialogOpen} />
                </Suspense>
                
                <Button size="sm" className="w-full" onClick={() => {
                  setMobileMenuOpen(false);
                  window.dispatchEvent(new CustomEvent('open-advisory-form'));
                }}>
                  <Mail className="h-4 w-4 mr-2" />
                  Request Strategic Briefing
                </Button>
              </div>
            </div>
          </div>}
      </header>
    </>;
};
